import { eventClient, events } from '@opus/web.core.lib.event-tracking'
import axios from 'axios'
import {
	apolloClient,
	CADENCE_CAPABILITIES_QUERY,
	GET_RECRUITER_PROFILE,
	GET_USER_INFO_QUERY,
	NOTIFICATION_CAPABILITIES_QUERY,
	REFRESH_WORKER_TOKEN,
} from '~/common/apollo'
import { EVENTS } from '~/common/constants'
import { store, computed, action, observable, persist, event } from '~/common/mobx.decorator'
import { notifyStore } from '~/stores/notify.store'
import { isUnsupportedFieldError } from '~/features/recruiter-cadences/candidates.utils'
// Circular with app.store (which imports authStore); safe because appStore is only
// read inside getters/actions, never while the module is being evaluated.
import { appStore } from './app.store'

const matchAnonymousEvents = async () => {
	// ONLY CALL THIS FUNCTION AFTER setUserId & setUserProperties
	try {
		const anonymousEvents = await window?.host?.getUserEvents()
		const anonymousId = await window?.host?.getId()

		const searchEvents = anonymousEvents
			.filter((event) => event.name === 'ANONYMOUS_SEARCH')
			.map(
				(event) =>
					new events.AnonymousSearchSuccessEvent({
						...event.data,
						createdAt: event.createdAt?.seconds * 1000 || new Date().getTime(),
						metadata: {
							ip: event.ip,
							user_agent: event.userAgent,
							anonymous_id: anonymousId,
						},
					})
			)

		searchEvents.forEach((event) => eventClient.logEvent(event))
		await window.host.archiveUserEvents()
		// IN CASE: You want to clean up all archived events
		// Please call: window.host.cleanUserEvents()
	} catch (error) {
		console.debug(error)
	}
}

class ProfileItem {
	@persist @observable id
	@persist @observable email
	@persist @observable firstName
	@persist @observable lastName
	@persist @observable phone
}

const DEFAULT_TOKEN_EXPIRE_IN = 30 * 60 * 1000 // calculate in miliseconds

@store({ persist: true })
class AuthStore {
	@persist @observable token = ''
	@persist @observable refreshToken = ''
	@persist @observable expiresIn = 0
	@persist @observable tokenType = ''
	@persist @observable expiresAt = 0
	@persist('list') @observable roles = []
	@persist('object', ProfileItem) @observable profile

	@observable currentJob = null
	// Kept out of persisted profile/roles: capabilities are tenant- and session-
	// scoped, and the API is the only authority for this administrative surface.
	@observable cadenceCapabilities = { read: false, edit: false, publish: false, deliveryEnabled: false }
	// Notification settings hub. `notificationCapabilitiesStatus` tells "not allowed" apart from "this backend has
	// no notification API" ('unsupported'): idle | ready | unsupported | error.
	@observable notificationCapabilities = { read: false, edit: false, enabled: false }
	@observable notificationCapabilitiesStatus = 'idle'
	@persist @observable notificationChannel = ''

	@observable isRefreshing = false
	@observable refreshPromise = null

	@action
	changeActiveTabIndex = (tabIndex) => {
		if (this.activeTabIndex === tabIndex) {
			return
		}

		this.prevActiveTabIndex = -1
		this.activeTabIndex = tabIndex
	}

	@action
	setUserRoles = (roles) => {
		this.roles = roles
	}

	@computed
	get recruiter() {
		return this.profile?.recruiter
	}

	@computed
	get authorization() {
		const isExpired = Date.now() > this.expiresAt
		return !this.token || isExpired ? '' : this.token
	}

	@computed
	get features() {
		return [
			'recruiter-home',
			// Maestro V2 (beta). The Route guard reads this list, so a screen absent
			// here renders the 403 page even though its route is registered.
			'recruiter-v2',
			'recruiter-role',
			// The User Roles table links every name here. The Route guard reads this
			// list, so without the code a click lands on a 403 even though the route
			// is registered and the user can see the table it came from.
			'recruiter-user-info',
			// Candidate App Access. Same silent failure as the two entries above if it
			// is missing: `careStore.getFeature` registers the route and gates it on
			// `canAccessRoleScreen`, so the sidebar link appears and the header renders
			// — and the Route guard answers the page itself with a 403.
			'recruiter-candidate-access',
			'recruiter-cadences',
			// Configuration › Notifications. Same silent 403 as above without it: the route is registered on the
			// flag, and the page itself decides read / edit from notificationCapabilities.
			'recruiter-notifications',
			'recruiter-assignments',
			'recruiter-jobs',
			'recruiter-job-info',
			'recruiter-candidate-info',
			'recruiter-candidates',
			// The results page is an entry point into jobs and candidates, not a
			// screen with permissions of its own. Without it here the Route guard
			// answers /search with a 403 even though the route is registered.
			'global-search',
			'recruiter-toas',
			'recruiter-report',
			// Applicant sources (SPT-2326). Registered on `maestro_applicant_source_tracking`; same silent 403 without it.
			'recruiter-applicant-sources',
			'recruiter-profile',
			// Job alerts (SPT-2331), the recruiter's own. The screens themselves say so when the client has them off.
			'recruiter-job-alerts',
			'care-jobs',
			'care-activation',
			'care-profile',
			'care-popular-cities',
			'care-find-job',
			'care-suggested-jobs',
			'care-my-jobs',
			'notification',
			'recruiter-messages',
			'common-change-password',
		]
	}

	@computed
	get enableHourlySearch() {
		return true
		// return this.profile?.company?.companyConfig?.enableHourlySearch || false
	}

	@computed
	get id() {
		return this.authorization && this.profile?.id
	}

	@computed
	get firstName() {
		return this.profile?.firstName
	}

	@computed
	get lastName() {
		return this.profile?.lastName
	}

	@computed
	get name() {
		return `${this.profile?.firstName} ${this.profile?.lastName}`
	}

	@computed
	get email() {
		return this.profile?.email
	}

	@computed
	get role() {
		if (this.roles) {
			return this.roles?.map((role) => role.name)?.join(', ')
		}
		return ''
	}

	@computed
	get fullName() {
		return this.name
	}

	@computed
	get activated() {
		return true
	}

	@computed
	get isAdmin() {
		return this.roles?.some((role) => role.scope === 'maestro_admin')
	}

	@computed
	get isManager() {
		return this.roles?.some((role) => role.scope === 'maestro_manager')
	}

	/** Same gate BE uses for `maestro_manage_all_workers?` — admin or manager. */
	@computed
	get managesAllWorkers() {
		return this.isAdmin || this.isManager
	}

	@computed
	get isAccountManager() {
		return this.roles?.some((role) => role.scope === 'maestro_account_manager')
	}

	@computed
	get canAccessRoleScreen() {
		// Only Admin and Manager can access role screen
		return this.isAdmin || this.isAccountManager
	}

	@computed
	get canUpdateUserRole() {
		// Only Admin can update user role
		return this.isAdmin
	}

	@computed
	get canCreateUser() {
		// Creating a user assigns a role, so it is strictly more powerful than
		// changing one - keep it to Admin, same as canUpdateUserRole. The backend
		// enforces this independently; this only hides the control.
		return this.isAdmin
	}

	@computed
	get canManageSavedViewDefaults() {
		// Setting a default view for a role affects everyone in it - keep it to
		// Admin, same as canUpdateUserRole/canCreateUser. BE enforces the write
		// via manage_all_workers?; this only hides the control.
		return this.isAdmin
	}

	// Defense in depth: the backend already answers false when `maestro_email_workflows`
	// is off, but the UI must not depend on that. The recruiter portal renders nothing
	// until appInit has loaded, so a false flag here means "off", not "still loading".
	@computed
	get canReadCadences() {
		return appStore.enableEmailWorkflows && this.cadenceCapabilities?.read === true
	}

	@computed
	get canEditCadences() {
		return this.canReadCadences && this.cadenceCapabilities?.edit === true
	}

	@computed
	get canPublishCadences() {
		return this.canReadCadences && this.cadenceCapabilities?.publish === true
	}

	// Same defence in depth as canReadCadences: the tenant flag, the API's own `enabled` and `read` must all agree.
	@computed
	get canReadNotifications() {
		const capabilities = this.notificationCapabilities
		return appStore.enableNotificationSettings && capabilities?.enabled === true && capabilities?.read === true
	}

	@computed
	get canEditNotifications() {
		return this.canReadNotifications && this.notificationCapabilities?.edit === true
	}

	// The care-notification Settings tab (the two job digests). On a tenant with `maestro_notification_settings` on,
	// only people who can edit Notifications may change it (spec §5.7, D14). Today's behaviour stays when the flag is
	// off, when the backend has no notification API, or when the API says the hub is off for the tenant; while the
	// answer is unknown the tab is read-only, because the server refuses a non-admin's change anyway.
	@computed
	get canChangeCareNotificationSettings() {
		if (!appStore.enableNotificationSettings) return true
		if (this.notificationCapabilitiesStatus === 'unsupported') return true
		if (this.notificationCapabilitiesStatus !== 'ready') return false
		if (this.notificationCapabilities?.enabled !== true) return true
		return this.notificationCapabilities?.edit === true
	}

	// Sent only when `maestro_notification_settings` is on. A backend without the notification API rejects the
	// field at validation time; that is 'unsupported', not an error, and the errorLink does not toast it.
	@action
	fetchNotificationCapabilities = async () => {
		const none = { read: false, edit: false, enabled: false }
		if (!appStore.enableNotificationSettings) {
			this.notificationCapabilities = none
			this.notificationCapabilitiesStatus = 'idle'
			return
		}
		try {
			const response = await apolloClient().query({ query: NOTIFICATION_CAPABILITIES_QUERY, fetchPolicy: 'no-cache', errorPolicy: 'all' })
			if (isUnsupportedFieldError(response?.errors)) {
				this.notificationCapabilities = none
				this.notificationCapabilitiesStatus = 'unsupported'
				return
			}
			const capability = response?.data?.notificationCapabilities
			this.notificationCapabilities = capability
				? { read: capability.read === true, edit: capability.edit === true, enabled: capability.enabled === true }
				: none
			this.notificationCapabilitiesStatus = capability ? 'ready' : 'error'
		} catch (error) {
			this.notificationCapabilities = none
			this.notificationCapabilitiesStatus = isUnsupportedFieldError(error) ? 'unsupported' : 'error'
		}
	}

	@action
	fetchCadenceCapabilities = async () => {
		if (!appStore.enableEmailWorkflows) {
			this.cadenceCapabilities = { read: false, edit: false, publish: false, deliveryEnabled: false }
			return
		}
		try {
			// no-cache: the shared client's cache also holds CADENCE_GRAPH_CAPABILITIES_QUERY's `cadenceCapabilities`.
			const response = await apolloClient().query({ query: CADENCE_CAPABILITIES_QUERY, fetchPolicy: 'no-cache' })
			const capability = response?.data?.cadenceCapabilities
			this.cadenceCapabilities = capability && capability.read === true ? capability : { read: false, edit: false, publish: false, deliveryEnabled: false }
		} catch (_) {
			this.cadenceCapabilities = { read: false, edit: false, publish: false, deliveryEnabled: false }
		}
	}

	@action
	fetchRecruiterProfile = async () => {
		const { id } = this.profile
		if (id) {
			const { data } = await apolloClient().query({
				query: GET_RECRUITER_PROFILE,
				variables: {
					id,
				},
			})
			if (data.recruiterProfile) {
				this.roles = data.recruiterProfile.roles
			}
		}
	}

	@action
	changeProfile = async (profile) => {
		if (this.profile && profile && this.profile?.id === profile?.id) {
			eventClient.logEvent(new events.OnboardingWorkerAuthorizedSuccessEvent())
		}

		// console.log(profile)

		if (profile) {
			this.profile = profile

			localStorage.setItem('profile', JSON.stringify(profile))

			eventClient.setUserId(profile.id)
			eventClient.setUserProperties({
				id: profile.id,
				email: profile.email,
				first_name: profile.firstName,
				last_name: profile.lastName,
				company_id: process.env.REACT_APP_COMPANY_ID,
			})

			// ONLY CALL THIS FUNCTION AFTER setUserId & setUserProperties
			// Fetch app config
			await matchAnonymousEvents()
		}

		// notifyStore.signal.push(() => {
		// 	notifyStore.signal.sendTags(profile)
		// 	notifyStore.signal.setExternalUserId(profile?.id)
		//})

		// Update LiveChat Info
		if (window.LC_API && typeof window.LC_API.set_visitor_name === 'function') {
			window.LC_API.set_visitor_name(this.name)
			window.LC_API.set_visitor_email(profile?.email)
		}
	}

	@action
	changeToken = async (authToken = {}) => {
		if (!authToken) return
		const { accessToken: token, expiresIn, refreshToken, tokenType } = authToken

		this.tokenType = tokenType
		this.token = token
		this.refreshToken = refreshToken
		this.expiresIn = (expiresIn ?? DEFAULT_TOKEN_EXPIRE_IN) * 1000
		this.expiresAt = Date.now() + this.expiresIn

		if (token) {
			axios.defaults.headers.common['Authorization'] = `${tokenType === 'bearer' ? 'Bearer' : ''} ${token}`
		} else {
			delete axios.defaults.headers.common['Authorization']
		}

		if (!token) {
			notifyStore.signal.push(() => {
				notifyStore.signal.setExternalUserId(null)
			})
		}
	}

	@action
	verifyToken = async () => {}

	@action
	getUserInfo = async () => {
		try {
			const response = await apolloClient().query({
				query: GET_USER_INFO_QUERY,
			})

			if (response?.data?.employer) {
				await this.changeProfile(response?.data?.employer)
				await this.setUserRoles(response?.data?.employer?.roles)
			}
		} catch (err) {
			console.error(err)
		}
	}

	@action
	refreshAuthToken = async () => {
		// Prevent multiple refresh calls
		if (this.isRefreshing) {
			return this.refreshPromise
		}

		this.isRefreshing = true
		try {
			this.refreshPromise = this._doRefreshToken()
			return await this.refreshPromise
		} finally {
			this.isRefreshing = false
			this.refreshPromise = null
		}
	}

	@action
	_doRefreshToken = async () => {
		if (!this.refreshToken) {
			throw new Error('No refresh token available')
		}

		try {
			const variables = { refreshToken: this.refreshToken }
			const {
				data: {
					refreshToken: { authToken },
				},
			} = await apolloClient().mutate({
				mutation: REFRESH_WORKER_TOKEN,
				variables,
				context: {
					clientName: 'public',
				},
			})

			await this.changeToken(authToken)
			await this.getUserInfo()

			return authToken
		} catch (error) {
			if (error.graphQLErrors?.[0]?.extensions?.code === 'REFRESH_TOKEN_EXPIRED') {
				await this.logout()
			}
			throw error
		}
	}

	@action
	resetPersist = () => {
		this.expiresAt = 0
		this.profile = null
		this.notificationChannel = ''
		this.currentJob = null
		this.cadenceCapabilities = { read: false, edit: false, publish: false, deliveryEnabled: false }
		this.notificationCapabilities = { read: false, edit: false, enabled: false }
		this.notificationCapabilitiesStatus = 'idle'
		this.phoneVerifiedAt = null
		sessionStorage.removeItem('apiUrl')
		sessionStorage.removeItem('tenantId')
	}

	@event(EVENTS.authStore.logout)
	async logout() {
		await this.changeToken({ accessToken: '' })
		await localStorage.removeItem('impersonateRecruiterId')
		await localStorage.removeItem('impersonatedRecruiter')
		await localStorage.removeItem('profile')
		await eventClient.setUserId(null)
		await eventClient.setUserProperties(null)
		await authStore.resetPersist()
		await eventClient.setUserProperties(null)
	}
}

export const authStore = new AuthStore()
